/**
 * Releases slice — action creators + reducers co-located.
 *
 * Stage 4 of the GitHub workspace rollout.
 */

import isEqual from 'lodash/isEqual';

import type {
  Branch,
  Environment,
  GitHubConfig,
  PullRequest,
  Release,
  ReleaseActivity,
} from '@/shared/types/releases.types';

/* ── Action creators ───────────────────────────────────────────────── */

export type ReleasesAction =
  | { type: 'RELEASES_SET'; releases: Release[] }
  | { type: 'RELEASES_UPSERT'; release: Release }
  | { type: 'RELEASES_REMOVE'; releaseId: string }
  | { type: 'BRANCHES_SET'; branches: Branch[] }
  | { type: 'BRANCHES_UPSERT'; branch: Branch }
  | { type: 'ENVS_SET'; environments: Environment[] }
  | { type: 'ENVS_UPSERT'; environment: Environment }
  | { type: 'ENVS_REMOVE'; envId: string }
  | { type: 'GITHUB_CONFIG_SET'; config: GitHubConfig }
  | { type: 'PRS_SET'; pullRequests: PullRequest[] }
  | { type: 'ACTIVITY_APPEND'; activities: ReleaseActivity[] }
  | { type: 'RELEASE_UI_SET_DRAWER'; releaseId: string | null }
  | { type: 'RELEASE_UI_SET_TAB'; tab: 'summary' | 'changes' | 'activity' }
  | { type: 'RELEASE_UI_SET_FILTER'; filter: string }
  | { type: 'RELEASE_UI_SET_SEARCH'; search: string };

export const setReleases = (releases: Release[]): ReleasesAction => ({
  type: 'RELEASES_SET',
  releases,
});

export const setBranches = (branches: Branch[]): ReleasesAction => ({
  type: 'BRANCHES_SET',
  branches,
});

/* ── Reducers ──────────────────────────────────────────────────────── */

export interface ReleasesState {
  byId: Record<string, Release>;
  /** Stable insertion order for stable iteration in selectors. */
  ids: string[];
}

const releasesInitial: ReleasesState = { byId: {}, ids: [] };

function releases(state: ReleasesState = releasesInitial, action: ReleasesAction): ReleasesState {
  switch (action.type) {
    case 'RELEASES_SET': {
      const byId: Record<string, Release> = {};
      const ids: string[] = [];
      for (const r of action.releases) {
        byId[r.id] = r;
        ids.push(r.id);
      }
      if (isEqual(byId, state.byId) && isEqual(ids, state.ids)) return state;
      return { byId, ids };
    }
    case 'RELEASES_UPSERT': {
      if (isEqual(state.byId[action.release.id], action.release)) return state;
      const ids = state.ids.includes(action.release.id)
        ? state.ids
        : [action.release.id, ...state.ids];
      return { byId: { ...state.byId, [action.release.id]: action.release }, ids };
    }
    case 'RELEASES_REMOVE': {
      if (!(action.releaseId in state.byId)) return state;
      const byId = { ...state.byId };
      delete byId[action.releaseId];
      return { byId, ids: state.ids.filter((id) => id !== action.releaseId) };
    }
    default:
      return state;
  }
}

export interface BranchesState {
  byName: Record<string, Branch>;
}

const branchesInitial: BranchesState = { byName: {} };

function branches(state: BranchesState = branchesInitial, action: ReleasesAction): BranchesState {
  switch (action.type) {
    case 'BRANCHES_SET': {
      const byName: Record<string, Branch> = {};
      for (const b of action.branches) byName[b.name] = b;
      if (isEqual(byName, state.byName)) return state;
      return { byName };
    }
    case 'BRANCHES_UPSERT': {
      if (isEqual(state.byName[action.branch.name], action.branch)) return state;
      return { byName: { ...state.byName, [action.branch.name]: action.branch } };
    }
    default:
      return state;
  }
}

export interface EnvironmentsState {
  byId: Record<string, Environment>;
  ids: string[];
}

const environmentsInitial: EnvironmentsState = { byId: {}, ids: [] };

function environments(
  state: EnvironmentsState = environmentsInitial,
  action: ReleasesAction,
): EnvironmentsState {
  switch (action.type) {
    case 'ENVS_SET': {
      const byId: Record<string, Environment> = {};
      const ids: string[] = [];
      for (const e of action.environments) {
        byId[e.id] = e;
        ids.push(e.id);
      }
      if (isEqual(byId, state.byId) && isEqual(ids, state.ids)) return state;
      return { byId, ids };
    }
    case 'ENVS_UPSERT': {
      if (isEqual(state.byId[action.environment.id], action.environment)) return state;
      const ids = state.ids.includes(action.environment.id)
        ? state.ids
        : [...state.ids, action.environment.id];
      return { byId: { ...state.byId, [action.environment.id]: action.environment }, ids };
    }
    case 'ENVS_REMOVE': {
      if (!(action.envId in state.byId)) return state;
      const byId = { ...state.byId };
      delete byId[action.envId];
      return { byId, ids: state.ids.filter((id) => id !== action.envId) };
    }
    default:
      return state;
  }
}

const githubConfigInitial: GitHubConfig = { connected: false, repo: '', defaultBranch: 'main' };

function githubConfig(
  state: GitHubConfig = githubConfigInitial,
  action: ReleasesAction,
): GitHubConfig {
  switch (action.type) {
    case 'GITHUB_CONFIG_SET':
      return isEqual(state, action.config) ? state : action.config;
    default:
      return state;
  }
}

export interface PullRequestsState {
  byNumber: Record<number, PullRequest>;
  numbers: number[];
}

const pullRequestsInitial: PullRequestsState = { byNumber: {}, numbers: [] };

function pullRequests(
  state: PullRequestsState = pullRequestsInitial,
  action: ReleasesAction,
): PullRequestsState {
  switch (action.type) {
    case 'PRS_SET': {
      const byNumber: Record<number, PullRequest> = {};
      const numbers: number[] = [];
      for (const p of action.pullRequests) {
        byNumber[p.number] = p;
        numbers.push(p.number);
      }
      if (isEqual(byNumber, state.byNumber) && isEqual(numbers, state.numbers)) return state;
      return { byNumber, numbers };
    }
    default:
      return state;
  }
}

export interface ReleaseActivityState {
  items: ReleaseActivity[];
}

const activityInitial: ReleaseActivityState = { items: [] };

function releaseActivity(
  state: ReleaseActivityState = activityInitial,
  action: ReleasesAction,
): ReleaseActivityState {
  switch (action.type) {
    case 'ACTIVITY_APPEND': {
      if (action.activities.length === 0) return state;
      const known = new Set(state.items.map((a) => a.id));
      const fresh = action.activities.filter((a) => !known.has(a.id));
      if (fresh.length === 0) return state;
      return { items: [...fresh, ...state.items] };
    }
    default:
      return state;
  }
}

export interface ReleaseUiState {
  drawerReleaseId: string | null;
  drawerTab: 'summary' | 'changes' | 'activity';
  filter: string;
  search: string;
}

const releaseUiInitial: ReleaseUiState = {
  drawerReleaseId: null,
  drawerTab: 'summary',
  filter: 'all',
  search: '',
};

function releaseUi(
  state: ReleaseUiState = releaseUiInitial,
  action: ReleasesAction,
): ReleaseUiState {
  switch (action.type) {
    case 'RELEASE_UI_SET_DRAWER':
      return state.drawerReleaseId === action.releaseId
        ? state
        : { ...state, drawerReleaseId: action.releaseId, drawerTab: 'summary' };
    case 'RELEASE_UI_SET_TAB':
      return state.drawerTab === action.tab ? state : { ...state, drawerTab: action.tab };
    case 'RELEASE_UI_SET_FILTER':
      return state.filter === action.filter ? state : { ...state, filter: action.filter };
    case 'RELEASE_UI_SET_SEARCH':
      return state.search === action.search ? state : { ...state, search: action.search };
    default:
      return state;
  }
}

export const releaseReducers = {
  releases,
  branches,
  environments,
  githubConfig,
  pullRequests,
  releaseActivity,
  releaseUi,
};
